import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import { Bell, Calendar, Filter, ExternalLink } from 'lucide-react';
import { shouldSuppressRateLimit } from '../utils/httpErrors';
import { COLORS } from '../config/theme';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor per aggiungere il token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const NotifichePage = () => {
  const { isAssociato, isAdmin, isGuest } = usePermissions();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isGuestUser = isGuest();
  
  // Verifica permessi
  useEffect(() => {
    if (!isAssociato() && !isAdmin() && !isGuest()) {
      navigate('/dashboard');
    }
  }, [isAssociato, isAdmin, isGuest, navigate]);
  
  const [jobs, setJobs] = useState([]);
  const [dates, setDates] = useState([]);
  const [gare, setGare] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [error, setError] = useState(null);
  
  const loadJobs = useCallback(async () => {
    setLoadingJobs(true);
    setError(null);
    try {
      const response = await api.get('/notifications/jobs');
      
      if (response.data && Array.isArray(response.data)) {
        setJobs(response.data);
        if (response.data.length === 0) {
          setError('Nessun job trovato. Verifica che esistano job di notifica attivi.');
        }
      } else {
        setJobs([]);
        setError('Risposta non valida dal server. Controlla i log del backend.');
      }
    } catch (error) {
      if (shouldSuppressRateLimit(error)) {
        setJobs([]);
        return;
      }
      setError(`Errore: ${error.response?.data?.detail || error.message}`);
      
      if (error.response?.status === 403) {
        navigate('/dashboard');
      }
      setJobs([]);
    } finally {
      setLoadingJobs(false);
    }
  }, [navigate]);

  // Carica job al mount
  useEffect(() => {
    if (isGuestUser) {
      // Guest: pagina visibile ma nessuna interazione / nessuna chiamata API notifiche
      setJobs([]);
      setDates([]);
      setGare([]);
      setSelectedJobId(null);
      setSelectedDate(null);
      setLoadingJobs(false);
      setError(null);
      return;
    }
    loadJobs();
  }, [loadJobs, isGuestUser]);
  
  // Pre-compila dai parametri URL DOPO che i job sono stati caricati
  useEffect(() => {
    if (loadingJobs || jobs.length === 0) {
      return;
    }
    
    const configId = searchParams.get('config_id');
    
    if (configId) {
      const configIdNum = parseInt(configId);
      const jobExists = jobs.some(job => job.id === configIdNum);
      if (jobExists && selectedJobId !== configIdNum) {
        setSelectedJobId(configIdNum);
      }
    }
  }, [searchParams, jobs, loadingJobs, selectedJobId]);
  
  // Carica date quando cambia job
  useEffect(() => {
    if (selectedJobId) {
      loadDates(selectedJobId);
    } else {
      setDates([]);
      setGare([]);
      setSelectedDate(null);
    }
  }, [selectedJobId]);
  
  // Imposta la data dai parametri URL DOPO che le date sono state caricate
  useEffect(() => {
    const date = searchParams.get('date');
    if (date && dates.length > 0 && selectedJobId) {
      const dateExists = dates.includes(date);
      if (dateExists && selectedDate !== date) {
        setSelectedDate(date);
      } else if (!dateExists && selectedDate === date) {
        setSelectedDate(null);
      }
    } else if (!date && selectedDate && selectedJobId) {
      if (dates.length > 0 && !dates.includes(selectedDate)) {
        setSelectedDate(null);
      }
    }
  }, [dates, searchParams, selectedJobId, selectedDate]);
  
  // Carica gare quando cambiano job e data
  useEffect(() => {
    if (selectedJobId && selectedDate) {
      loadGare(selectedJobId, selectedDate);
    } else {
      setGare([]);
    }
  }, [selectedJobId, selectedDate]);
  
  const loadDates = async (configId) => {
    try {
      const response = await api.get(`/notifications/dates/${configId}`);
      
      if (response.data && Array.isArray(response.data)) {
        setDates(response.data);
      } else {
        setDates([]);
      }
    } catch (error) {
      setDates([]);
    }
  };
  
  const loadGare = async (configId, date) => {
    setLoading(true);
    try {
      const response = await api.get(`/notifications/gare/${configId}/${date}`);
      setGare(response.data);
    } catch (error) {
      console.error('Errore caricamento gare:', error);
      setGare([]);
    } finally {
      setLoading(false);
    }
  };
  
  if (!isAssociato() && !isAdmin() && !isGuest()) {
    return null;
  }
  
  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
          {/* Header Section */}
          <div className="mb-8">
            <h1 
              className="text-4xl font-bold mb-4 flex items-center gap-3" 
              style={{ color: COLORS.text }}
            >
              <Bell className="h-10 w-10" style={{ color: COLORS.primary }} />
              Notifiche Email
            </h1>
            <p className="text-lg" style={{ color: COLORS.text2 }}>
              Visualizza le gare inviate tramite email
            </p>
          </div>
          
          {/* Error Message */}
          {error && (
            <div
              className="rounded-xl p-4 mb-8 border"
              style={{ 
                backgroundColor: '#FEF2F2', 
                borderColor: COLORS.error 
              }}
            >
              <p className="font-medium" style={{ color: COLORS.error }}>
                {error}
              </p>
            </div>
          )}
          
          {/* Filtri Card */}
          <div
            className="bg-white rounded-2xl p-6 mb-8 shadow-[0_8px_24px_rgba(0,0,0,0.05)]"
            style={{ border: `1px solid ${COLORS.border}` }}
          >
            {isGuestUser && (
              <div
                className="mb-4 rounded-xl border px-4 py-3 text-sm"
                style={{ borderColor: COLORS.border, backgroundColor: 'rgba(148,163,184,0.15)', color: COLORS.text }}
              >
                Stai visualizzando la sezione <strong>Notifiche</strong> in modalità <strong>Guest</strong>.
                Puoi vedere la struttura della pagina, ma non puoi selezionare pianificazioni o date.
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Selezione Job */}
              <div>
                <label 
                  className="block text-sm font-semibold mb-2 flex items-center gap-2" 
                  style={{ color: COLORS.text }}
                >
                  <Filter className="h-4 w-4" style={{ color: COLORS.primary }} />
                  Seleziona Pianificazione
                </label>
                {loadingJobs ? (
                  <div 
                    className="w-full px-3 py-2 rounded-lg border bg-gray-50 text-gray-500"
                    style={{ borderColor: COLORS.border }}
                  >
                    Caricamento job...
                  </div>
                ) : (
                  <select
                    value={selectedJobId || ''}
                    onChange={(e) => setSelectedJobId(e.target.value ? parseInt(e.target.value) : null)}
                    disabled={isGuestUser}
                    className={[
                      "w-full px-3 py-2 rounded-lg border text-sm",
                      "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                      "disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed",
                    ].join(' ')}
                    style={{
                      borderColor: COLORS.border,
                      color: COLORS.text,
                    }}
                  >
                    <option value="">-- Seleziona una Pianificazione --</option>
                    {jobs.map(job => (
                      <option key={job.id} value={job.id}>
                        {job.name} {job.notifications_count > 0 ? `(${job.notifications_count} notifiche)` : '(nessuna notifica ancora)'}
                      </option>
                    ))}
                    {jobs.length > 0 && (
                      <option disabled className="text-gray-400">
                        ────────────────────────
                      </option>
                    )}
                  </select>
                )}
                {jobs.length === 0 && !loadingJobs && !isGuestUser && (
                  <div className="mt-2">
                    <p className="text-sm font-medium" style={{ color: COLORS.error }}>
                      ⚠️ Nessun job con notifiche disponibile.
                    </p>
                    <p className="text-sm mt-1" style={{ color: COLORS.text2 }}>
                      Verifica che:
                    </p>
                    <ul className="text-sm mt-1 list-disc list-inside" style={{ color: COLORS.text2 }}>
                      <li>Esistano job di notifica attivi nella pagina Scheduler</li>
                      <li>I job abbiano inviato almeno una notifica con gare</li>
                      <li>I job appartengano al tuo account</li>
                    </ul>
                    <p className="text-sm mt-2" style={{ color: COLORS.text2 }}>
                      Controlla la console del browser (F12) per dettagli.
                    </p>
                  </div>
                )}
              </div>
              
              {/* Selezione Data */}
              <div>
                <label 
                  className="block text-sm font-semibold mb-2 flex items-center gap-2"
                  style={{ color: COLORS.text }}
                >
                  <Calendar className="h-4 w-4" style={{ color: COLORS.primary }} />
                  Seleziona Data
                </label>
                <select
                  value={selectedDate || ''}
                  onChange={(e) => setSelectedDate(e.target.value || null)}
                  disabled={isGuestUser || !selectedJobId || dates.length === 0}
                  className={[
                    "w-full px-3 py-2 rounded-lg border text-sm",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                    "disabled:bg-gray-50 disabled:text-gray-400",
                    "disabled:cursor-not-allowed",
                  ].join(' ')}
                  style={{
                    borderColor: COLORS.border,
                    color: COLORS.text,
                  }}
                >
                  <option value="">-- Seleziona una data --</option>
                  {dates.map(date => (
                    <option key={date} value={date}>
                      {new Date(date).toLocaleDateString('it-IT', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </option>
                  ))}
                </select>
                {selectedJobId && dates.length === 0 && !loading && (
                  <p className="mt-2 text-sm" style={{ color: COLORS.text2 }}>
                    Nessuna data disponibile per questo job
                  </p>
                )}
              </div>
            </div>
          </div>
          
          {/* Loading State */}
          {loading && (
            <div className="text-center py-12">
              <div
                className="animate-spin rounded-full h-12 w-12 mx-auto border-b-2"
                style={{ borderColor: COLORS.primary }}
              />
              <p className="mt-4 text-sm" style={{ color: COLORS.text2 }}>
                Caricamento gare…
              </p>
            </div>
          )}
          
          {/* Risultati */}
          {!loading && gare.length > 0 && (
            <div 
              className="bg-white rounded-2xl shadow-[0_8px_24px_rgba(0,0,0,0.05)] overflow-hidden"
              style={{ border: `1px solid ${COLORS.border}` }}
            >
              <div 
                className="p-6 border-b"
                style={{ 
                  backgroundColor: COLORS.softBlue,
                  borderColor: COLORS.border 
                }}
              >
                <h2 className="text-xl font-semibold" style={{ color: COLORS.text }}>
                  {gare.length} {gare.length === 1 ? 'gara trovata' : 'gare trovate'}
                </h2>
                <p className="text-sm mt-1" style={{ color: COLORS.text2 }}>
                  Gare inviate il {selectedDate && new Date(selectedDate).toLocaleDateString('it-IT', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {gare.map(gara => (
                    <div
                      key={gara.id}
                      className="border rounded-xl p-5 transition-all hover:shadow-md"
                      style={{ 
                        borderColor: COLORS.border,
                        backgroundColor: '#FFFFFF'
                      }}
                    >
                      <h3 className="text-lg font-semibold mb-2">
                        <button
                          onClick={() => navigate(`/storico-gare/${gara.id}?from=notifiche&config_id=${selectedJobId}&date=${selectedDate}`)}
                          className="text-left font-semibold text-base flex items-center gap-2
                                     focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
                                     transition-colors hover:opacity-80"
                          style={{ color: COLORS.primary }}
                        >
                          {gara.title}
                          <ExternalLink className="h-4 w-4" />
                        </button>
                      </h3>
                      {gara.description && (
                        <p className="mb-3 line-clamp-2" style={{ color: COLORS.text2 }}>
                          {gara.description}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-4 text-sm" style={{ color: COLORS.text2 }}>
                        {gara.value && (
                          <span className="flex items-center gap-1">
                            <strong style={{ color: COLORS.text }}>
                              {gara.value.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })}
                            </strong>
                          </span>
                        )}
                        {gara.deadline && (
                          <span className="flex items-center gap-1">
                            Scadenza: <strong style={{ color: COLORS.text }}>
                              {new Date(gara.deadline).toLocaleDateString('it-IT')}
                            </strong>
                          </span>
                        )}
                        {gara.provincia && (
                          <span className="flex items-center gap-1">
                            {gara.provincia}{gara.regione && `, ${gara.regione}`}
                          </span>
                        )}
                        {gara.pubblicazione && (
                          <span className="flex items-center gap-1">
                            Pubblicazione: {gara.pubblicazione}
                          </span>
                        )}
                        <span 
                          className="px-2 py-1 rounded-full text-xs font-semibold"
                          style={{
                            backgroundColor: gara.status === 'active' ? '#D1FAE5' :
                                            gara.status === 'expired' ? '#FEE2E2' :
                                            gara.status === 'archived' ? COLORS.border :
                                            COLORS.softYellow,
                            color: gara.status === 'active' ? '#065F46' :
                                   gara.status === 'expired' ? '#991B1B' :
                                   gara.status === 'archived' ? COLORS.text2 :
                                   '#92400E'
                          }}
                        >
                          {gara.status === 'active' ? 'Attivo' : 
                           gara.status === 'expired' ? 'Scaduto' : 
                           gara.status === 'archived' ? 'Archiviato' : 
                           'Aggiudicato'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          {/* Empty States */}
          {!loading && selectedJobId && selectedDate && gare.length === 0 && (
            <div 
              className="bg-white border rounded-xl p-6 text-center"
              style={{ 
                borderColor: COLORS.accent,
                backgroundColor: COLORS.softYellow 
              }}
            >
              <p className="font-medium" style={{ color: '#92400E' }}>
                Nessuna gara trovata per questa selezione.
              </p>
            </div>
          )}
          
          {!loading && !selectedJobId && !selectedDate && (
            <div 
              className="bg-white border rounded-xl p-6 text-center"
              style={{ 
                borderColor: COLORS.border,
                backgroundColor: COLORS.softBlue 
              }}
            >
              <p style={{ color: COLORS.primary }}>
                Seleziona una Pianificazione e una data per visualizzare le notifiche email
              </p>
            </div>
          )}
    </div>
  );
};

export default NotifichePage;